import { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider } from 'react-redux';

import Header from './components/Header';
import ProductDetails from './components/Product/ProductDetails';
import PaymentResponse from './components/Cart/PaymentResponse';
import Cart from './components/Cart';
import Login from './components/Login';
import { ColorModeContext } from './context/color-context';
import './App.scss';
import { deepOrange, grey, blue } from '@mui/material/colors';
import { COLORS } from './utils/ColorsTheme';
import Footer from './components/Footer';
import NotFound from './components/NotFound';
import MainContainer from './components/MainContainer';
import setupStore from './store/store';

const store = setupStore();
export const LocationDisplay = () => {
  const location = useLocation();
  return (
    <div data-testid='location-display' className='hidden'>
      {location.pathname}
    </div>
  );
};

function App() {
  const prefersDarkMode = localStorage.getItem('mode') === 'light' ? 'light' : 'dark';
  const [mode, setMode] = useState<string>(prefersDarkMode);
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode: string) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: mode === 'dark' ? 'dark' : 'light',
          ...(mode === 'light'
            ? {
                // palette values for light mode
                primary: deepOrange,
                background: {
                  default: blue[100],
                  paper: COLORS.darkModeColor,
                },
                text: {
                  primary: grey[900],
                  secondary: grey[800],
                },
              }
            : {
                // palette values for dark mode
                primary: deepOrange,
                background: {
                  default: COLORS.defaultDarkMode,
                  paper: COLORS.secondaryDarkMode,
                },
                text: {
                  primary: COLORS.darkModeColor,
                  secondary: grey[500],
                },
              }),
        },
      }),
    [mode],
  );
  useEffect(() => {
    localStorage.setItem('mode', mode);
  }, [mode]);

  return (
    <Provider store={store}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Header />
          <main style={{ marginTop: 10 }}>
            <Routes>
              <Route path='/' element={<MainContainer />} />
              <Route path='/product-details/:id' element={<ProductDetails />} />
              <Route path='/cart' element={<Cart />} />
              <Route path='/payment-response' element={<PaymentResponse />} />
              <Route path='/login' element={<Login />} />
              <Route path='*' element={<NotFound />} />
            </Routes>
            <LocationDisplay />
          </main>
          <Footer />
        </ThemeProvider>
      </ColorModeContext.Provider>
    </Provider>
  );
}

export default App;
